import { ComponentChildren } from 'preact';
import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { Button } from '../UNSAFE_Button';
import { ChevronLeft } from '../UNSAFE_RedwoodIcons/ChevronLeft';
import { ChevronRight } from '../UNSAFE_RedwoodIcons/ChevronRight';
import { classNames as clsx } from '../utils/UNSAFE_classNames';
import { styles } from './themes/ConveyorBeltStyles.css';
import { useUser } from '../hooks/UNSAFE_useUser';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { xUnits } from '../UNSAFE_Theme';

export interface ConveyorBeltProps {
  /**
   * ConveyorBelt Component content
   */
  children?: ComponentChildren;
  /**
   * Sets the number of pixels that an element's content is scrolled from its initial position.
   */
  scrollPosition?: number;
  /**
   * Callback that is executed every time conveyor belt is scrolled and the scroll position is changed.
   * @param value The value is the new scroll position of the conveyor belt.
   * @returns
   */
  onScrollPositionChanged?: (value?: number) => void;
  /**
   * Indicates whether overflow content arrows are visible or hidden.
   * "auto" show overflow arrows on desktop, hide on mobile.
   * "visible" always show overflow arrows.
   * "hidden" never show overflow arrows.
   */
  arrowVisibility?: 'auto' | 'visible' | 'hidden';
}

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if runnning on a mobile device, false otherwise
 */
function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}

function scrollPrevious(
  direction: 'ltr' | 'rtl',
  overflowContainer?: HTMLDivElement | null,
  scrollAmount?: number
) {
  if (overflowContainer && scrollAmount) {
    if (direction === 'ltr') {
      overflowContainer.scrollTo({
        left: overflowContainer.scrollLeft - scrollAmount
      });
    } else {
      overflowContainer.scrollTo({
        left: overflowContainer.scrollLeft + scrollAmount
      });
    }
  }
}

function scrollNext(
  direction: 'ltr' | 'rtl',
  overflowContainer?: HTMLDivElement | null,
  scrollAmount?: number
) {
  if (overflowContainer && scrollAmount) {
    if (direction === 'ltr') {
      overflowContainer.scrollTo({
        left: overflowContainer.scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    } else {
      overflowContainer.scrollTo({
        left: overflowContainer.scrollLeft - scrollAmount,
        behavior: 'smooth'
      });
    }
  }
}
/**
 * The Conveyor belt component is a container element that manages
 * overflow for its child elements and allows scrolling among them
 */
export const ConveyorBelt = ({
  children,
  scrollPosition,
  onScrollPositionChanged,
  arrowVisibility = 'auto'
}: ConveyorBeltProps) => {
  const [canPaginateNext, setCanPaginateNext] = useState(false);
  const [canPaginatePrevious, setCanPaginatePrevious] = useState(false);
  const overflowContainerRef = useRef<HTMLDivElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const nextButtonContainerRef = useRef<HTMLDivElement | null>(null);
  const prevButtonContainerRef = useRef<HTMLDivElement | null>(null);
  const baseRef = useRef<HTMLDivElement | null>(null);
  const leftRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<any>(null);
  const { direction } = useUser();
  const [buttonsHidden, setButtonsHidden] = useState(
    (isMobile() && arrowVisibility === 'auto') || arrowVisibility === 'hidden'
  );

  const scrollEndHandler = useCallback(() => {
    if (onScrollPositionChanged) onScrollPositionChanged(overflowContainerRef.current?.scrollLeft);
  }, [onScrollPositionChanged]);

  useEffect(() => {
    setButtonsHidden((isMobile() && arrowVisibility === 'auto') || arrowVisibility === 'hidden');
  }, [arrowVisibility]);

  useEffect(() => {
    if (!overflowContainerRef.current) return;
    overflowContainerRef.current.scrollTo({
      left: scrollPosition
    });
  }, [scrollPosition]);

  useEffect(() => {
    const overflowContainer = overflowContainerRef.current;
    const leftGuard = leftRef.current;
    const rightGuard = rightRef.current;

    if (overflowContainer && leftGuard && rightGuard) {
      if (!buttonsHidden) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target.getAttribute('data-intersection-id') === 'left') {
                setCanPaginatePrevious(!entry.isIntersecting);
              } else {
                setCanPaginateNext(!entry.isIntersecting);
              }
            });
          },
          {
            root: overflowContainer
          }
        );
        observerRef.current.observe(leftGuard);
        observerRef.current.observe(rightGuard);
      }
      overflowContainer.addEventListener('scrollend', scrollEndHandler);
    }

    // Clean-up
    return () => {
      if (!overflowContainer) return;
      overflowContainer.removeEventListener('scrollend', scrollEndHandler);
      observerRef.current.unobserve(leftGuard);
      observerRef.current.unobserve(rightGuard);
    };
  }, [buttonsHidden, scrollEndHandler]);

  return (
    <div ref={baseRef} className={clsx([styles.baseStyle])}>
      {canPaginatePrevious && !buttonsHidden && (
        <div
          aria-hidden={true}
          ref={prevButtonContainerRef}
          class={clsx([
            styles.buttonContainer,
            direction === 'ltr' ? styles.previousButton : styles.nextButton
          ])}>
          <TabbableModeContext.Provider value={{ isTabbable: false }}>
            <Button
              startIcon={direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />}
              variant="borderless"
              onAction={() =>
                scrollPrevious(
                  direction,
                  overflowContainerRef.current,
                  overflowContainerRef.current?.clientWidth
                )
              }
            />
          </TabbableModeContext.Provider>
        </div>
      )}
      <div
        style={{
          clipPath: direction === 'ltr' ? `inset(0rem ${canPaginateNext ? xUnits(12) : '0rem'} 0rem ${
            canPaginatePrevious ? xUnits(12) : 0
          })` : `inset(0rem ${canPaginatePrevious ? xUnits(12) : '0rem'} 0rem ${
            canPaginateNext ? xUnits(12) : 0
          })`
        }}
        className={styles.overflowContainer}
        ref={overflowContainerRef}>
        <div className={styles.contentContainer} ref={contentContainerRef}>
          <div data-intersection-id="left" ref={leftRef} style={{ minWidth: 1 }} />
          {children}
          <div data-intersection-id="right" ref={rightRef} style={{ minWidth: 1 }} />
        </div>
      </div>
      {canPaginateNext && !buttonsHidden && (
        <div
          aria-hidden={true}
          ref={nextButtonContainerRef}
          class={clsx([
            styles.buttonContainer,
            direction === 'ltr' ? styles.nextButton : styles.previousButton
          ])}>
          <TabbableModeContext.Provider value={{ isTabbable: false }}>
            <Button
              startIcon={direction === 'ltr' ? <ChevronRight /> : <ChevronLeft />}
              variant="borderless"
              onAction={() =>
                scrollNext(
                  direction,
                  overflowContainerRef.current,
                  overflowContainerRef.current?.clientWidth
                )
              }
            />
          </TabbableModeContext.Provider>
        </div>
      )}
    </div>
  );
};
